import React, { useState } from "react";

const OrderModal = ({ order, isOpen, onClose, onStatusUpdate }) => {
  const [newStatus, setNewStatus] = useState(order?.status || "");
  const [message, setMessage] = useState("");
  const [courierType, setCourierType] = useState(
    order?.data?.courierType || ""
  );
  const [trackingId, setTrackingId] = useState(order?.data?.trackingId || "");

  if (!isOpen || !order) return null;

  const statusOptions = [
    "PENDING",
    "CONFIRMED",
    "SHIPPED",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "CANCELLED",
    "RETURN_PENDING",
    "RETURNED",
  ];

  const courierOptions = [
    "BLUE_DART",
    "DTDC",
    "DELHIVERY",
    "XPRESSBEES",
    "ECOM_EXPRESS",
    "GATI",
    "FIRST_FLIGHT",
    "INDIA_POST",
    "OTHER",
  ];

  const getStatusColor = (status) => {
    const colors = {
      PENDING: "bg-yellow-100 text-yellow-800",
      CONFIRMED: "bg-blue-100 text-blue-800",
      SHIPPED: "bg-purple-100 text-purple-800",
      OUT_FOR_DELIVERY: "bg-orange-100 text-orange-800",
      DELIVERED: "bg-green-100 text-green-800",
      CANCELLED: "bg-red-100 text-red-800",
      RETURN_PENDING: "bg-amber-100 text-amber-800",
      RETURNED: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      let response;
      if (order.status === "PENDING") {
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/orders/verify-payment/user/${order.razorpay_order_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "*/*",
            },
          }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/orders/${order.razorpay_order_id}/status`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              accept: "*/*",
            },
            body: JSON.stringify({
              status: newStatus,
              message: message || `Status updated to ${newStatus}`,
              data: {
                courierType,
                trackingId,
              },
            }),
          }
        );
      }

      if (response.ok) {
        onStatusUpdate();
        onClose();
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Order Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mt-4">
          <p className="text-sm text-gray-500">
            Order ID: {order.razorpay_order_id}
          </p>
          <p className="text-sm text-gray-500">Receipt: {order.receipt}</p>
        </div>

        {/* Shipping Address Section */}
        {order.other_info?.shipping && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-sm font-semibold mb-2 text-gray-700">Shipping Address</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Name:</span> {order.other_info.shipping.name}</div>
              <div><span className="font-medium">Phone:</span> {order.other_info.shipping.phone || order.other_info.shipping.phone_number}</div>
              <div><span className="font-medium">Email:</span> {order.other_info.shipping.email}</div>
              <div><span className="font-medium">City:</span> {order.other_info.shipping.city}</div>
              <div><span className="font-medium">State:</span> {order.other_info.shipping.state}</div>
              <div><span className="font-medium">Country:</span> {order.other_info.shipping.country}</div>
              <div><span className="font-medium">Postal Code:</span> {order.other_info.shipping.postal_code}</div>
              <div className="col-span-2"><span className="font-medium">Address:</span> {order.other_info.shipping.street_address}, {order.other_info.shipping.additional_address_line} {order.other_info.shipping.landmarks?.join(', ')}</div>
            </div>
          </div>
        )}

        {/* Distributor Shop Details */}
        {(order.other_info?.shopInfo || order.other_info?.placedByDistributorId) && (
          <div className="mt-4 bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <div className="text-sm font-bold mb-2 text-emerald-800 flex items-center gap-1.5">
              <span>🏪 Shop / Distributor Outlet Info</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
              <div><span className="font-medium text-gray-900">Shop Name:</span> {order.other_info.shopInfo?.shopName || 'PGM Distributor Store'}</div>
              <div><span className="font-medium text-gray-900">Shop Phone:</span> {order.other_info.shopInfo?.shopPhone || '—'}</div>
              <div><span className="font-medium text-gray-900">GST Number:</span> {order.other_info.shopInfo?.shopGst || 'N/A'}</div>
              <div><span className="font-medium text-gray-900">Distributor ID:</span> <span className="font-mono text-xs">{order.other_info.placedByDistributorId || order.other_info.shopInfo?.distributorId || '—'}</span></div>
              {order.other_info.shopInfo?.shopAddress && (
                <div className="col-span-2"><span className="font-medium text-gray-900">Shop Address:</span> {order.other_info.shopInfo.shopAddress}</div>
              )}
            </div>
          </div>
        )}
        <h4 className="font-medium mb-2">Products</h4>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {order.other_info?.productInfo?.map((product, idx) => (
            <div key={product.id || idx} className="bg-white border rounded-lg shadow-sm min-w-[220px] max-w-[220px] flex-shrink-0 flex flex-col items-center p-3 hover:shadow-md transition">
              <img
                src={`https://product.pgmbusiness.com/uploads/products/${product.image}`}
                alt={product.name}
                className="w-20 h-20 object-contain rounded mb-2 border"
                onError={e => { e.target.onerror = null; e.target.src = '/no-image.png'; }}
              />
              <div className="font-semibold text-gray-800 text-center text-sm line-clamp-2 mb-1">{product.name}</div>
              <div className="text-xs text-gray-500 mb-1">{product.brand}</div>
              <div className="flex items-center gap-2 text-xs mb-1">
                <span className="bg-indigo-50 text-indigo-700 rounded px-2 py-0.5">Qty: {product.quantity}</span>
                <span className="bg-green-50 text-green-700 rounded px-2 py-0.5">₹{product.price}</span>
              </div>
              <div className="text-xs text-gray-400 text-center line-clamp-2">{product.category}</div>
            </div>
          ))}
        </div>

      <div className="mt-4 space-y-4">
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Update Order Status
          </h4>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 border-2"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {(newStatus === "SHIPPED" ||
              newStatus === "OUT_FOR_DELIVERY") && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Courier Service
                    </label>
                    <select
                      value={courierType}
                      onChange={(e) => setCourierType(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 border-2"
                      required
                    >
                      <option value="">Select Courier Service</option>
                      {courierOptions.map((courier) => (
                        <option key={courier} value={courier}>
                          {courier}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tracking ID
                    </label>
                    <input
                      type="text"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 border-2"
                      placeholder="Enter tracking number"
                      required
                    />
                  </div>
                </>
              )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Update Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 border-2"
                rows="2"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          Update Status
        </button>
      </div>
    </div>
  </div>
  );
};

export default OrderModal;
